import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ArrowRight, BookOpen, CheckCircle2, HelpCircle, Bot } from "lucide-react";
import { StudyModule } from "@/hooks/useStudyTrail";
import { LessonRenderer } from "./LessonRenderer";
import { InlineQuiz } from "./InlineQuiz";
import { TutorDrawer } from "./TutorDrawer";
import { ModuleSummaryCard } from "./ModuleSummaryCard";

interface ModuleContentProps {
  module: StudyModule;
  onBack: () => void;
  onComplete: (accuracy: number) => void;
  inline?: boolean;
  initialStep?: number;
}

type StepType = "intro" | "quiz" | "exam";

interface Step {
  type: StepType;
  title: string;
  data?: any;
}

export function ModuleContent({ module, onBack, onComplete, inline, initialStep }: ModuleContentProps) {
  const [currentStep, setCurrentStep] = useState(initialStep ?? 0);
  const [tutorOpen, setTutorOpen] = useState(false);

  const { data: quizzes = [] } = useQuery({
    queryKey: ["module-quizzes", module.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quizzes")
        .select("*")
        .eq("module_id", module.id)
        .eq("is_active", true);
      if (error) throw error;
      return (data || []).map((q: any) => ({
        ...q,
        options: typeof q.options === "string" ? JSON.parse(q.options) : q.options,
      }));
    },
  });

  // Build sequential steps: Aula (lesson_content) → 3 questões → prova final
  const steps = useMemo<Step[]>(() => {
    const result: Step[] = [];
    const questionsBeforeExam = 3;

    if (module.lesson_content) {
      result.push({ type: "intro", title: "Aula" });
    }

    const practiceQuizzes = quizzes.slice(0, questionsBeforeExam);
    practiceQuizzes.forEach((quiz: any, idx: number) => {
      result.push({
        type: "quiz",
        title: `Exercício ${idx + 1}`,
        data: quiz,
      });
    });

    const examQuizzes = quizzes.slice(questionsBeforeExam);
    if (examQuizzes.length > 0) {
      result.push({
        type: "exam",
        title: "Prova do Módulo",
        data: examQuizzes,
      });
    }

    return result;
  }, [quizzes, module.lesson_content]);

  useEffect(() => {
    if (initialStep !== undefined && initialStep < steps.length) {
      setCurrentStep(initialStep);
    }
  }, [initialStep, steps.length]);

  const step = steps[currentStep];
  const totalSteps = steps.length;

  const goNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      onComplete(100);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const [prevModuleId, setPrevModuleId] = useState(module.id);
  if (module.id !== prevModuleId) {
    setPrevModuleId(module.id);
    setCurrentStep(0);
  }

  if (!step) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Conteúdo será adicionado em breve.</p>
        </CardContent>
      </Card>
    );
  }

  const stepIcon = {
    intro: <BookOpen className="h-4 w-4" />,
    quiz: <HelpCircle className="h-4 w-4" />,
    exam: <CheckCircle2 className="h-4 w-4" />,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="gap-1 text-xs">
            {stepIcon[step.type]}
            Etapa {currentStep + 1}/{totalSteps}
          </Badge>
          <h2 className={inline ? "text-lg font-bold text-foreground" : "text-xl font-bold text-foreground"}>
            {step.title}
          </h2>
        </div>
        {module.progress_status === "completed" && (
          <Badge variant="success" className="gap-1 text-xs">
            <CheckCircle2 className="h-3 w-3" /> Concluída
          </Badge>
        )}
      </div>

      <ModuleSummaryCard
        moduleId={module.id}
        moduleTitle={module.title}
        category={module.category}
        lessonContent={module.lesson_content}
        videoTitles={[]}
      />

      {step.type === "intro" && module.lesson_content && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-5 w-5 text-primary" />
              <h3 className="font-bold text-lg">Aula</h3>
            </div>
            <LessonRenderer content={module.lesson_content} />
          </CardContent>
        </Card>
      )}

      {step.type === "quiz" && step.data && (
        <InlineQuiz
          quizId={step.data.id}
          question={step.data.question}
          options={Array.isArray(step.data.options) ? step.data.options : []}
          correctAnswer={step.data.correct_answer}
          explanation={step.data.explanation}
          difficulty={step.data.difficulty}
          category={module.category}
          moduleTitle={module.title}
        />
      )}

      {step.type === "exam" && Array.isArray(step.data) && (
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <h3 className="font-bold text-lg">Prova do Módulo</h3>
                <Badge variant="outline" className="text-xs">{step.data.length} questões</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Responda todas as questões abaixo para concluir o módulo.
              </p>
            </CardContent>
          </Card>
          {step.data.map((quiz: any) => (
            <InlineQuiz
              key={quiz.id}
              quizId={quiz.id}
              question={quiz.question}
              options={Array.isArray(quiz.options) ? quiz.options : []}
              correctAnswer={quiz.correct_answer}
              explanation={quiz.explanation}
              difficulty={quiz.difficulty}
              category={module.category}
              moduleTitle={module.title}
            />
          ))}
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t">
        <Button
          variant="ghost"
          size="sm"
          onClick={goPrev}
          disabled={currentStep === 0}
          className="gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Etapa Anterior
        </Button>

        {currentStep < totalSteps - 1 ? (
          <Button onClick={goNext} className="gap-1">
            Próxima Etapa
            <ArrowRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={() => onComplete(100)} className="gap-1">
            <CheckCircle2 className="h-4 w-4" />
            Marcar como Concluída
          </Button>
        )}
      </div>

      <Button
        onClick={() => setTutorOpen(true)}
        size="lg"
        className="fixed bottom-6 right-24 sm:right-28 z-40 rounded-full shadow-lg gap-2 h-14 px-5 bg-gradient-to-r from-primary to-primary/80 hover:scale-105 transition-transform"
      >
        <Bot className="h-5 w-5" />
        <span className="hidden sm:inline font-semibold">Tutor IA</span>
      </Button>

      <TutorDrawer
        isOpen={tutorOpen}
        onClose={() => setTutorOpen(false)}
        moduleId={module.id}
        moduleTitle={module.title}
        category={module.category}
        contextType={step.type === "quiz" || step.type === "exam" ? "quiz" : "module"}
        contextRefId={step.type === "quiz" ? step.data?.id : undefined}
        lessonContent={module.lesson_content || undefined}
      />
    </div>
  );
}
